import React, { useEffect, useState } from 'react';
import {
  Box,
  Alert,
  CircularProgress,
  Divider,
  Fade,
  MenuItem,
  Select,
  SelectChangeEvent,
  styled,
  Switch,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';

import { useMobileSettingsNavigation } from './shared/hooks/useMobileSettingsNavigation';
import MobilePageContent from 'app/pages/mobile/mobile-layout/MobilePageContent';
import { BACKGROUND_SIZES_v2 } from 'app/pages/mobile/utils/constants';
import { useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { selectUserProfile } from 'app/store/userProfileSlice';
import { useUserRole } from 'app/hooks/useUserRole';
// SEE-1232 row m: the caller's own preferences read and write CDK AppSync (userService). The
// superseded documents stay in graphql_custom/notifications (SEE-1238 removes them).
import { userService } from 'app/services/user.service';
import { captureException } from '@sentry/react';

type TxnReviewFrequency = 'INSTANT' | 'DAILY_DIGEST' | 'WEEKLY_DIGEST';

type Prefs = {
  id?: string;
  userId: string;
  paidolId?: string | null;
  transactionAlertSms?: boolean;
  transactionAlertEmail?: boolean;
  cardLockedSms?: boolean;
  cardLockedEmail?: boolean;
  manualCardActionSms?: boolean;
  manualCardActionEmail?: boolean;
  cardActivationSms?: boolean;
  cardActivationEmail?: boolean;
  statementReadySms?: boolean;
  statementReadyEmail?: boolean;
  txnReviewFrequency?: TxnReviewFrequency;
  txnReviewCodeUpdateSms?: boolean;
  txnReviewCodeUpdateEmail?: boolean;
  txnReviewNoteUpdateSms?: boolean;
  txnReviewNoteUpdateEmail?: boolean;
  txnReviewReceiptUpdateSms?: boolean;
  txnReviewReceiptUpdateEmail?: boolean;
  txnReviewReadySms?: boolean;
  txnReviewReadyEmail?: boolean;
  returnedTransactionSms?: boolean;
  returnedTransactionEmail?: boolean;
  hasReceivedWelcomeSms?: boolean;
};

type NotificationRow = {
  key: string;
  label: string;
  description?: string;
  icon: typeof ReceiptLongOutlinedIcon;
  emailKey: keyof Prefs;
  smsKey: keyof Prefs;
};

const IOSSwitch = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#324C80',
        opacity: 1,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === 'dark' ? '#8B8B8D' : '#D1D1D6',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

const DEFAULT_PREFERENCES = {
  transactionAlertSms: false,
  transactionAlertEmail: false,
  cardLockedSms: false,
  cardLockedEmail: true,
  manualCardActionSms: false,
  manualCardActionEmail: true,
  cardActivationSms: false,
  cardActivationEmail: true,
  statementReadySms: false,
  statementReadyEmail: true,
  txnReviewFrequency: 'DAILY_DIGEST' as TxnReviewFrequency,
  txnReviewCodeUpdateSms: false,
  txnReviewCodeUpdateEmail: false,
  txnReviewNoteUpdateSms: false,
  txnReviewNoteUpdateEmail: false,
  txnReviewReceiptUpdateSms: false,
  txnReviewReceiptUpdateEmail: false,
  // SEE-1295: opt-in, as on web.
  txnReviewReadySms: false,
  txnReviewReadyEmail: false,
};

function MobileNotificationSettingsPage(): JSX.Element {
  const { isAdministrator, isApprovalAdmin, canViewTxnReviewNotifications } = useUserRole();
  // SEE-946: aligns mobile with web (Primary + Group + Approval; not Secondary).
  const showTxnReviewSection = canViewTxnReviewNotifications;

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { handleBackSettings } = useMobileSettingsNavigation();
  const { profile } = useAppSelector(selectUserProfile);
  const { selectedPaidol, user } = useAppSelector(selectUserCompanies);

  const userId = (profile?.id as string) || (user?.id as string) || '';
  const paidolId = (selectedPaidol?.id as string) || undefined;

  const [loading, setLoading] = useState(false);
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  // SEE-1312: a failed read must not paint the system defaults, or the next
  // toggle autosaves them over the user's stored preferences.
  const [loadError, setLoadError] = useState(false);

  // `revert` holds the pre-toggle value of the key a handler changed. On a
  // rejected write only that key is restored (a concurrent toggle that did
  // persist, or hasReceivedWelcomeSms set by the welcome SMS, is left alone) and
  // the user is told. Migration writes made by load() pass no revert: they are
  // not a change the user made, so they are captured but not announced.
  const savePreferences = async (updatedPrefs: Prefs, revert?: Partial<Prefs>) => {
    if (!updatedPrefs) return;

    try {
      let isCreate = !updatedPrefs.id;

      const allowed = {
        transactionAlertSms: !!updatedPrefs.transactionAlertSms,
        transactionAlertEmail: !!updatedPrefs.transactionAlertEmail,
        cardLockedSms: !!updatedPrefs.cardLockedSms,
        cardLockedEmail: !!updatedPrefs.cardLockedEmail,
        manualCardActionSms: !!updatedPrefs.manualCardActionSms,
        manualCardActionEmail: !!updatedPrefs.manualCardActionEmail,
        cardActivationSms: !!updatedPrefs.cardActivationSms,
        cardActivationEmail: !!updatedPrefs.cardActivationEmail,
        statementReadySms: !!updatedPrefs.statementReadySms,
        statementReadyEmail: !!updatedPrefs.statementReadyEmail,
        txnReviewFrequency: updatedPrefs.txnReviewFrequency || 'DAILY_DIGEST',
        txnReviewCodeUpdateSms: !!updatedPrefs.txnReviewCodeUpdateSms,
        txnReviewCodeUpdateEmail: !!updatedPrefs.txnReviewCodeUpdateEmail,
        txnReviewNoteUpdateSms: !!updatedPrefs.txnReviewNoteUpdateSms,
        txnReviewNoteUpdateEmail: !!updatedPrefs.txnReviewNoteUpdateEmail,
        txnReviewReceiptUpdateSms: !!updatedPrefs.txnReviewReceiptUpdateSms,
        txnReviewReceiptUpdateEmail: !!updatedPrefs.txnReviewReceiptUpdateEmail,
        txnReviewReadySms: !!updatedPrefs.txnReviewReadySms,
        txnReviewReadyEmail: !!updatedPrefs.txnReviewReadyEmail,
        returnedTransactionSms: !!updatedPrefs.returnedTransactionSms,
        returnedTransactionEmail: !!updatedPrefs.returnedTransactionEmail,
      } as const;

      if (isCreate) {
        // Create errors when the caller already has a row, so check first.
        const existing = await userService.getMyNotificationPreferences<Prefs>();
        if (existing) {
          isCreate = false;
        }
      }

      if (isCreate) {
        // SEE-1232 row m: `id` and `userId` are not inputs — CDK sets both to the caller.
        const createInput = {
          paidolId: paidolId || null,
          ...allowed,
        };
        const created = await userService.createMyNotificationPreferences<Prefs>(createInput);
        if (created) setPrefs(created);
      } else {
        // SEE-1312: never send userId on update — the field is create/read only
        // for its owner, so a user cannot re-point their row at someone else.
        // SEE-1232 row m: nor `id` — CDK resolves the row from the caller.
        const updateInput = {
          paidolId: paidolId || null,
          ...allowed,
        };
        const updated = await userService.updateMyNotificationPreferences<Prefs>(updateInput);
        if (updated) setPrefs(updated);
      }
    } catch (error) {
      captureException(error);
      if (!revert) return;
      setPrefs((current) => (current ? { ...current, ...revert } : current));
      enqueueSnackbar(t('notificationPreferencesSaveFailed'), { variant: 'error' });
    }
  };

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      // SEE-1232 row m: CDK resolves the caller's own row. The list-by-userId fallback for
      // legacy rows with a different id is gone — no dev stage holds one.
      const data = (await userService.getMyNotificationPreferences<Prefs>()) ?? null;

      const mergedPrefs = data
        ? {
            ...DEFAULT_PREFERENCES,
            ...data,
            userId,
            paidolId: paidolId || null,
          }
        : {
            userId,
            paidolId: paidolId || null,
            ...DEFAULT_PREFERENCES,
          };

      setPrefs(mergedPrefs);

      if (data && !data.id) {
        await savePreferences(mergedPrefs);
      } else if (
        data &&
        (data.manualCardActionEmail === undefined ||
          data.manualCardActionSms === undefined ||
          data.cardActivationEmail === undefined ||
          data.cardActivationSms === undefined ||
          data.statementReadyEmail === undefined ||
          data.statementReadySms === undefined ||
          data.txnReviewFrequency === undefined)
      ) {
        await savePreferences(mergedPrefs);
      }
    } catch (error) {
      setLoadError(true);
      captureException(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const sendWelcomeSmsIfNeeded = async (key: string, checked: boolean) => {
    const isSmsKey = key.endsWith('Sms') && key !== 'hasReceivedWelcomeSms';
    if (!isSmsKey || !checked || prefs?.hasReceivedWelcomeSms) return;

    const phone = (profile as Record<string, unknown>)?.phone_number as string | undefined;
    if (!phone) return;

    try {
      await userService.sendWelcomeSms({ userId, phone });
      setPrefs((prev) => (prev ? { ...prev, hasReceivedWelcomeSms: true } : prev));
    } catch (error) {
      captureException(error);
    }
  };

  const onToggle = (key: keyof Prefs) => async (_: unknown, checked: boolean) => {
    if (!prefs || loadError) return;
    const revert = { [key]: prefs[key] } as Partial<Prefs>;
    const updatedPrefs = { ...prefs, [key]: checked };
    setPrefs(updatedPrefs);
    await sendWelcomeSmsIfNeeded(key, checked);
    await savePreferences(updatedPrefs, revert);
  };

  const onFrequencyChange = async (event: SelectChangeEvent<TxnReviewFrequency>) => {
    if (!prefs || loadError) return;
    const revert = { txnReviewFrequency: prefs.txnReviewFrequency };
    const updatedPrefs = { ...prefs, txnReviewFrequency: event.target.value as TxnReviewFrequency };
    setPrefs(updatedPrefs);
    await savePreferences(updatedPrefs, revert);
  };

  if (loadError) {
    return (
      <MobilePageContent onBack={handleBackSettings} title={t('notifications')}>
        <Box p={2}>
          <Alert severity="error">{t('notificationPreferencesLoadFailed')}</Alert>
        </Box>
      </MobilePageContent>
    );
  }

  if (loading && !prefs) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <Fade in timeout={300}>
          <CircularProgress size={100} sx={{ color: 'primary.main' }} />
        </Fade>
      </Box>
    );
  }

  const receiptAgentEnabled = Boolean(selectedPaidol?.receiptAgentSettings?.isEnabled);

  const notificationRows: NotificationRow[] = [
    {
      key: 'transaction-alert',
      label: receiptAgentEnabled
        ? `${t('newTransactions')} (AI Receipt Agent Enabled)`
        : t('newTransactions'),
      icon: ReceiptLongOutlinedIcon,
      emailKey: 'transactionAlertEmail',
      smsKey: 'transactionAlertSms',
    },
    {
      key: 'card-activation',
      label: t('cardActivations'),
      icon: CreditCardOutlinedIcon,
      emailKey: 'cardActivationEmail',
      smsKey: 'cardActivationSms',
    },
    {
      key: 'card-auto-lock',
      label: t('cardAutoLockAlerts'),
      icon: LockOutlinedIcon,
      emailKey: 'cardLockedEmail',
      smsKey: 'cardLockedSms',
    },
    {
      key: 'manual-card-action',
      label: t('manualCardLocksUnlocks'),
      icon: AdminPanelSettingsOutlinedIcon,
      emailKey: 'manualCardActionEmail',
      smsKey: 'manualCardActionSms',
    },
    ...(isAdministrator
      ? [
          {
            key: 'statement-ready',
            label: t('monthlyStatement'),
            icon: DescriptionOutlinedIcon,
            emailKey: 'statementReadyEmail' as keyof Prefs,
            smsKey: 'statementReadySms' as keyof Prefs,
          },
        ]
      : []),
    {
      key: 'returned-transaction',
      label: t('returnedTransactions', 'Returned transactions'),
      description: t(
        'returnedTransactionsDescription',
        'Get notified when your transaction is returned for correction.'
      ),
      icon: ReceiptLongOutlinedIcon,
      emailKey: 'returnedTransactionEmail',
      smsKey: 'returnedTransactionSms',
    },
  ];

  const txnReviewRows: NotificationRow[] = [
    {
      key: 'txn-review-ready',
      label: isApprovalAdmin ? t('txnReviewReadyForApproval') : t('txnReviewReadyForReview'),
      icon: PlaylistAddCheckOutlinedIcon,
      emailKey: 'txnReviewReadyEmail',
      smsKey: 'txnReviewReadySms',
    },
    {
      key: 'txn-review-code',
      label: t('txnReviewCodeUpdates'),
      icon: CodeOutlinedIcon,
      emailKey: 'txnReviewCodeUpdateEmail',
      smsKey: 'txnReviewCodeUpdateSms',
    },
    {
      key: 'txn-review-note',
      label: t('txnReviewNoteUpdates'),
      icon: NotesOutlinedIcon,
      emailKey: 'txnReviewNoteUpdateEmail',
      smsKey: 'txnReviewNoteUpdateSms',
    },
    {
      key: 'txn-review-receipt',
      label: t('txnReviewReceiptUpdates'),
      icon: ReceiptOutlinedIcon,
      emailKey: 'txnReviewReceiptUpdateEmail',
      smsKey: 'txnReviewReceiptUpdateSms',
    },
  ];

  const frequencyOptions = [
    { value: 'INSTANT' as TxnReviewFrequency, label: t('instant') },
    { value: 'DAILY_DIGEST' as TxnReviewFrequency, label: t('dailyDigest') },
    { value: 'WEEKLY_DIGEST' as TxnReviewFrequency, label: t('weeklyDigest') },
  ];

  const renderRow = (row: NotificationRow) => (
    <Box
      key={row.key}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.5,
        px: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
          width: '100%',
        }}
      >
        <Typography id={`${row.key}-label`} sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }}>
          {row.label}
        </Typography>
        {row.description && (
          <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>{row.description}</Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          <IOSSwitch
            inputProps={{ 'aria-labelledby': `${row.key}-label` }}
            checked={!!prefs?.[row.smsKey]}
            onChange={onToggle(row.smsKey)}
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          <IOSSwitch
            inputProps={{ 'aria-labelledby': `${row.key}-label` }}
            checked={!!prefs?.[row.emailKey]}
            onChange={onToggle(row.emailKey)}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <MobilePageContent
      transparentHeight={BACKGROUND_SIZES_v2.SHOW_HEADER}
      title={t('mobileNotificationSettingsTitle')}
      onBack={handleBackSettings}
    >
      <Box display="flex" flexDirection="column" gap={3} maxWidth={900} sx={{ py: 2 }}>
        <Box
          sx={{
            overflow: 'hidden',
            width: '100%',
            position: 'relative',
          }}
        >
          <Box sx={{ position: 'absolute' }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '350px 1px 120px 120px',
              alignItems: 'center',
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'end', gap: 4, marginBottom: 2, px: 1.5 }}>
            <Typography fontSize="14px" fontWeight={600} sx={{ color: 'primary.main' }}>
              SMS
            </Typography>
            <Typography fontSize="14px" fontWeight={600} sx={{ color: 'primary.main' }}>
              {t('email')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {notificationRows.map(renderRow)}
          </Box>

          {/* Transaction Reviews section - Group Admin + Administrator only */}
          {showTxnReviewSection && (
            <>
              <Divider sx={{ my: 2 }} />

              {/* Section header */}
              <Box sx={{ px: 1.5, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <RateReviewOutlinedIcon sx={{ width: 18, height: 18, color: 'primary.main' }} />
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'primary.main' }}>
                    {t('transactionReviews')}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary', mb: 1 }}>
                  {t('transactionReviewsDescription')}
                </Typography>
                <Select<TxnReviewFrequency>
                  value={prefs?.txnReviewFrequency || 'DAILY_DIGEST'}
                  onChange={onFrequencyChange}
                  size="small"
                  fullWidth
                  sx={{
                    fontSize: '13px',
                    '& .MuiSelect-select': { py: 0.75 },
                  }}
                >
                  {frequencyOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '13px' }}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              {/* Transaction review trigger rows */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  borderLeft: '3px solid',
                  borderColor: 'primary.main',
                  ml: 1.5,
                }}
              >
                {txnReviewRows.map(renderRow)}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </MobilePageContent>
  );
}

export default MobileNotificationSettingsPage;
